/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { convertChannelCasing } from 'lib-shared-api-utils';
import type {
  ConversationBulkActionType,
  ReadConversationApiResponse,
  ReadConversationBulkActionApiResponse,
  ReadConversationBulkActionFailedActionsApiResponse,
  ReadConversationMessageDeliveryEventsResponse,
  ReadConversationMessagesApiResponse,
  ReadConversationsApiResponse,
  ReadScheduledConversationBatchesApiResponse,
} from 'lib-shared-types-api';

import {
  transformConversation,
  transformConversationStringifiedProperties,
  transformMainConversation,
  transformRequesterConversation,
} from 'utils/conversation/transformConversation';

import { conversationBulkActionTypeMap } from './conversationBulkActionTypeMap';

/**
 * Transforms the conversation endpoint response.
 *
 * @param response The response from the endpoint.
 */
export function transformReadConversationResponse(
  response: any,
): ReadConversationApiResponse {
  transformMainConversation(response.data.history.main_conversation);
  response.data.requester_conversations.forEach(({ conversation }) =>
    transformRequesterConversation(conversation),
  );

  response.data.history.linked_conversations.forEach(({ conversation }) =>
    transformConversation(conversation),
  );

  if (response.data.history.linked_to) {
    transformConversation(response.data.history.linked_to.conversation);
  }

  return response;
}

/**
 * Transforms the conversations endpoint response.
 *
 * @param response The response from the endpoint.
 */
export function transformReadConversationsResponse(
  response: any,
): ReadConversationsApiResponse {
  response.data.conversations.forEach(({ conversation }) =>
    transformConversation(conversation),
  );

  return response;
}

/**
 * Transforms the conversation messages endpoint response.
 *
 * @param response The response from the endpoint.
 */
export function transformReadConversationMessagesResponse(
  response: any,
): ReadConversationMessagesApiResponse {
  response.data.messages.forEach((message) => {
    message.channel = convertChannelCasing(message.channel, 'snake');
    transformConversationStringifiedProperties(message.attributes);
  });

  return response;
}

/**
 * Transforms the scheduled conversation batches endpoint response.
 *
 * @param response The response from the endpoint.
 */
export function transformReadScheduledConversationBatchesResponse(
  response: any,
): ReadScheduledConversationBatchesApiResponse {
  // The response seems to be ordered "oldest first", which isn't great for the UI, so I'm reversing
  // it in order to show the most recent scheduled batches first.
  response.data.batches.reverse();
  return response;
}

/**
 * Transforms the conversation message delivery events endpoint response.
 *
 * @param response The response from the endpoint.
 */
export function transformReadConversationMessageDeliveryEvents(
  response: any,
): ReadConversationMessageDeliveryEventsResponse {
  return {
    data: {
      delivery_events: response,
    },
    meta: {},
  };
}

/**
 * Transforms the API response of a bulk action type, to that which the frontend
 * uses internally, and throws an error if it encounters an unknown type.
 */
const transformConversationBulkActionType = (
  type: string,
): ConversationBulkActionType => {
  const convertedActionType = conversationBulkActionTypeMap[type];

  if (!convertedActionType) {
    throw new Error(
      `Unknown bulk action type '${type}'. Please add it to the conversationBulkActionTypeMap.`,
    );
  }

  return convertedActionType;
};

/**
 * Transforms the conversation bulk action endpoint response.
 *
 * @param response The transformed response.
 */
export function transformReadConversationBulkAction(
  response: any,
): ReadConversationBulkActionApiResponse {
  response.data.action_type = transformConversationBulkActionType(
    response.data.action_type,
  );

  return response;
}

/**
 * Transforms the conversation bulk action failed actions endpoint response.
 *
 * @param response The transformed response.
 */
export function transformReadConversationBulkActionFailedActions(
  response: any,
): ReadConversationBulkActionFailedActionsApiResponse {
  response.data.failed_actions.forEach((failedAction) => {
    failedAction.action_type = transformConversationBulkActionType(
      failedAction.action_type,
    );
  });

  return response;
}
